import {
  QueryKey,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";
import React from "react";
import { GroupBase, OptionsOrGroups } from "react-select";
import { LoadOptions, Response } from "react-select-async-paginate";
import { OptionType } from "./select-input";
import { useCurrentRef } from "@/hooks/use-current-ref";
import { isDefined } from "@/lib/utils";

type MutationLoaderProps<
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
  TData,
  TError,
  TVariables,
  TContext,
> = {
  mutation: UseMutationResult<TData, TError, TVariables, TContext>;
  requestVariable: (
    search: string | undefined,
    page: number,
    limit?: number,
    additional?: { exact?: boolean },
  ) => TVariables | null;
  createOptions: (data: Awaited<TData>) => Option[];
  hasMore: (data: Awaited<TData>, variable: TVariables) => boolean;
};

export const useOptionsMutationLoader = <
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
  TData,
  TError,
  TVariables,
  TContext,
>({
  mutation,
  createOptions,
  requestVariable,
  hasMore,
}: MutationLoaderProps<
  Value,
  Option,
  IsMulti,
  Group,
  TData,
  TError,
  TVariables,
  TContext
>) => {
  return React.useCallback(
    async (search, _current, additional: { page: number }) => {
      const page = additional?.page ?? 1;
      // const ret = await devices.promise;
      const variable = requestVariable(search || undefined, page);
      if (variable === null) {
        return {
          additional: { page: page + 1 },
          options: [],
          hasMore: false,
        };
      }
      const data = await mutation.mutateAsync(variable);
      return {
        additional: { page: page + 1 },
        options: createOptions(data),
        hasMore: hasMore(data, variable),
      };
    },
    [],
  ) as LoadOptions<Option, Group, { page: number }>;
};

type QueryLoaderProps<
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
  TVariables,
  TQueryFnData,
  TError,
  TData,
  TQueryKey extends QueryKey,
> = {
  queryCreator: (
    argument: TVariables,
  ) => UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>;
  requestVariable: (
    search: string | undefined,
    page: number,
    limit?: number,
    additional?: { exact?: boolean },
  ) => TVariables | null;
  createOptions: (data: Awaited<TData>) => Option[];
  hasMore: (data: Awaited<TData>, variable: TVariables) => boolean;
  perPage?: number;
  prefixOptions?: Option[];
};

export const useOptionsQueryLoader = <
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
  TVariables,
  TQueryFnData,
  TError,
  TData,
  TQueryKey extends QueryKey,
>({
  queryCreator,
  createOptions,
  requestVariable,
  hasMore,
  perPage,
  prefixOptions,
}: QueryLoaderProps<
  Value,
  Option,
  IsMulti,
  Group,
  TVariables,
  TQueryFnData,
  TError,
  TData,
  TQueryKey
>) => {
  const [state, setState] = React.useState<
    | {
        search: string;
        current: OptionsOrGroups<Option, Group>;
        additional?: { page: number };
        resolve: (value: Response<Option, Group, { page: number }>) => void;
      }
    | undefined
  >(undefined);
  const { resolve, search, current, additional } = state ?? {};
  const page = additional?.page ?? 1;
  const variable = !isDefined(search)
    ? null
    : requestVariable(search || undefined, page, perPage);
  const query = useQuery({
    // experimental_prefetchInRender: true,
    ...(variable
      ? queryCreator(variable)
      : ({ enabled: false } as UseQueryOptions<
          TQueryFnData,
          TError,
          TData,
          TQueryKey
        >)),
  });
  if (resolve && variable !== undefined) {
    if (variable === null) {
      resolve({
        additional: { page: page + 1 },
        options: [],
        hasMore: false,
      });
    } else if (query.data) {
      let options = createOptions(query.data as Awaited<TData>);
      if (prefixOptions && prefixOptions.length > 0 && page <= 1 && !search) {
        options = [
          ...prefixOptions,
          ...options.filter(
            (op) => !prefixOptions.find((b) => b.value === op.value),
          ),
        ];
      }
      resolve({
        additional: { page: page + 1 },
        options,
        hasMore: hasMore(query.data as Awaited<TData>, variable),
      });
    }
  }
  return React.useCallback((search, current, additional?: { page: number }) => {
    return new Promise<Response<Option, Group, { page: number }>>((resolve) => {
      setState({ search, current, additional, resolve });
    });
  }, []) as LoadOptions<Option, Group, { page: number }>;
};

type OptionsCacheProps<
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
  TVariables,
  TQueryFnData,
  TError,
  TData,
  TQueryKey extends QueryKey,
  TReverseVariables,
  TReverseQueryFnData,
  TReverseError,
  TReverseData,
  TReverseQueryKey extends QueryKey,
  TQueryKeySearch extends QueryKey,
  TQueryReverseSearch extends QueryKey,
> = {
  queryKey?: TQueryKeySearch;
  queryCreator: (
    argument: TVariables,
  ) => UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>;
  requestVariable: (
    search: string | undefined,
    page: number,
    limit?: number,
    additional?: { exact?: boolean },
  ) => TVariables | null;
  createOptions: (data: Awaited<TData>) => Option[];
  hasMore: (data: Awaited<TData>, variable: TVariables) => boolean;
  reverseQueryKey?: TQueryReverseSearch;
  reverseRequestVariable?: (
    results: any[] | undefined,
    page: number,
    limit?: number,
  ) => TReverseVariables | null;
  reverseQueryCreator?: (
    argument: TReverseVariables,
  ) => UseQueryOptions<
    TReverseQueryFnData,
    TReverseError,
    TReverseData,
    TReverseQueryKey
  >;
  reverseCreateOptions?: (data: Awaited<TReverseData>) => Option[];
  prefixOptions?: Option[];
};

const compareUntil = (a: any[], b: any[]) => {
  for (let i = 0; i < b.length; i++) {
    if (i >= a.length) return true;
    if (b[i] !== a[i]) return false;
  }
  return true;
};

export const inferOptionsCache =
  <
    Value,
    Option extends OptionType<Value> = OptionType<Value>,
    IsMulti extends boolean = boolean,
    Group extends GroupBase<Option> = GroupBase<Option>,
  >() =>
  <
    TVariables,
    TQueryFnData,
    TError,
    TData,
    TQueryKey extends QueryKey,
    TReverseVariables,
    TReverseQueryFnData,
    TReverseError,
    TReverseData,
    TReverseQueryKey extends QueryKey,
    TQueryKeySearch extends QueryKey,
    TQueryReverseSearch extends QueryKey,
  >(
    props: OptionsCacheProps<
      Value,
      Option,
      IsMulti,
      Group,
      TVariables,
      TQueryFnData,
      TError,
      TData,
      TQueryKey,
      TReverseVariables,
      TReverseQueryFnData,
      TReverseError,
      TReverseData,
      TReverseQueryKey,
      TQueryKeySearch,
      TQueryReverseSearch
    > & { valueType?: unknown },
  ) =>
    props;

export const useOptionsCache = <
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
  TVariables,
  TQueryFnData,
  TError,
  TData,
  TQueryKey extends QueryKey,
  TReverseVariables,
  TReverseQueryFnData,
  TReverseError,
  TReverseData,
  TReverseQueryKey extends QueryKey,
  TQueryKeySearch extends QueryKey,
  TQueryReverseSearch extends QueryKey,
>({
  queryKey, // used for label -> id mapping
  createOptions,
  requestVariable,
  queryCreator,
  reverseQueryKey, // used for id -> label mapping, irrelevant if label === id
  reverseRequestVariable,
  reverseQueryCreator,
  reverseCreateOptions = createOptions as any,
  prefixOptions,
}: OptionsCacheProps<
  Value,
  Option,
  IsMulti,
  Group,
  TVariables,
  TQueryFnData,
  TError,
  TData,
  TQueryKey,
  TReverseVariables,
  TReverseQueryFnData,
  TReverseError,
  TReverseData,
  TReverseQueryKey,
  TQueryKeySearch,
  TQueryReverseSearch
>) => {
  const queryClient = useQueryClient();
  const queryCache = queryClient.getQueryCache();
  const createOptionsRef = useCurrentRef(createOptions);
  const reverseCreateOptionsRef = useCurrentRef(reverseCreateOptions);
  const makeMaps = React.useCallback(
    (
      options: Option[],
      maps?: {
        // forward: Record<Option["value"], Option>;
        forward: Record<any, Option>;
        reverse: Record<Option["label"], Option>;
      },
    ) => {
      const {
        // forward = {} as Record<Option["value"], Option>,
        forward = {} as Record<any, Option>,
        reverse = {} as Record<Option["label"], Option>,
      } = maps ?? {};
      options.map((option) => {
        forward[option.value as any] = option;
        reverse[option.label] = option;
      });
      return { forward, reverse };
    },
    [],
  );
  const [cache, setCache] = React.useState(
    React.useCallback(() => {
      const optionsFromForward = !queryKey
        ? []
        : queryCache
            .findAll({ queryKey })
            .filter((q) => {
              return q.state.status == "success";
            })
            .flatMap((q) =>
              createOptionsRef.current(q.state.data as Awaited<TData>),
            );
      const optionsFromBackwards =
        !reverseQueryKey ||
        !queryKey ||
        !compareUntil(reverseQueryKey as any, queryKey as any)
          ? []
          : queryCache
              .findAll({ queryKey: reverseQueryKey })
              .filter((q) => {
                return q.state.status == "success";
              })
              .flatMap((q) =>
                reverseCreateOptionsRef.current(
                  q.state.data as Awaited<TReverseData>,
                ),
              );
      let maps = makeMaps(optionsFromForward);
      maps = makeMaps(optionsFromBackwards, maps);
      if (prefixOptions) {
        maps = makeMaps(prefixOptions, maps);
      }
      return maps;
    }, [
      queryKey,
      reverseQueryKey,
      createOptionsRef,
      reverseCreateOptionsRef,
      prefixOptions,
    ]),
  );
  const ref = React.useRef<any>();
  const updateCache = React.useCallback(
    (options: Option[]) => {
      setCache((cache) => makeMaps(options, cache));
    },
    [setCache],
  );
  const findPartial = React.useMemo(() => {
    if (reverseQueryCreator && reverseRequestVariable) {
      return async (partial: any[]) => {
        const variable = reverseRequestVariable(partial, 1, partial.length);
        if (!variable) return [];
        const data = await queryClient.ensureQueryData(
          reverseQueryCreator(variable),
        );
        const options = reverseCreateOptionsRef.current(
          data as unknown as Awaited<TReverseData>,
        );
        updateCache(options);
        return options;
      };
    } else {
      // if reverseQuery* not provided, attempt to get partial from the main query
      return async (partial: any[]) => {
        const variable = requestVariable(undefined, 1, 999);
        if (!variable) return [];
        const req = queryCreator(variable);
        const data = await queryClient.ensureQueryData(req);
        const options = createOptionsRef.current(
          data as unknown as Awaited<TData>,
        );
        updateCache(options);
        return options;
      };
    }
  }, [
    requestVariable,
    queryCreator,
    createOptionsRef,
    reverseRequestVariable,
    reverseQueryCreator,
    reverseCreateOptionsRef,
    updateCache,
  ]);

  const findMissing = React.useCallback(
    async (missing: string[]) => {
      return (
        await Promise.allSettled(
          missing.map(async (search) => {
            const variable = requestVariable(search || undefined, 1, 1, {
              exact: true,
            });
            if (!variable) return [];
            const req = queryCreator(variable);
            const data = await queryClient.ensureQueryData(req);
            const options = createOptionsRef.current(
              data as unknown as Awaited<TData>,
            );
            updateCache(options);
            return options;
          }),
        )
      ).flatMap((v) => (v.status == "fulfilled" ? v.value : []));
    },
    [requestVariable, queryCreator, createOptionsRef, updateCache],
  );

  React.useEffect(
    () =>
      !queryKey
        ? undefined
        : queryCache.subscribe((event) => {
            const query = event.query;
            if (
              !query ||
              query.state.status !== "success" ||
              !compareUntil(query.queryKey as any, queryKey as any)
            ) {
              return;
            }
            if (ref.current && ref.current === query) {
              return;
            }
            ref.current = query;
            const options = createOptionsRef.current(
              query.state.data as Awaited<TData>,
            );
            updateCache(options);
          }),
    [queryKey],
  );
  return {
    cache,
    setCache,
    updateCache,
    findMissing,
    findPartial,
  };
};
